import { create } from 'zustand';
import { storage, onRehydrate } from '../utils/storage';

const INITIAL_STATE = storage.get('properties', []);

const usePropertyStore = create((set, get) => ({
    properties: INITIAL_STATE,

    addProperty: (property) => {
        const newProperty = {
            id: crypto.randomUUID(),
            createdAt: new Date().toISOString(),
            options: [], // Default for select/multi-select
            ...property
        };
        const next = [...get().properties, newProperty];
        set({ properties: next });
        storage.set('properties', next);
        return newProperty.id;
    },

    updateProperty: (id, updates) => {
        const next = get().properties.map(p =>
            p.id === id ? { ...p, ...updates } : p
        );
        set({ properties: next });
        storage.set('properties', next);
    },

    deleteProperty: (id) => {
        const next = get().properties.filter(p => p.id !== id);
        set({ properties: next });
        storage.set('properties', next);
    },

    reorderProperties: (orderedIds) => {
        const current = get().properties;
        const next = orderedIds
            .map(id => current.find(p => p.id === id))
            .filter(Boolean);
        
        // Append any missing properties (just in case)
        const missing = current.filter(p => !orderedIds.includes(p.id));
        const finalNext = [...next, ...missing];
        
        set({ properties: finalNext });
        storage.set('properties', finalNext);
    },

    /**
     * Add a new option to a select/multi-select property on-the-fly.
     * Returns the new option object.
     */
    addOptionToProperty: (propertyId, option) => {
        const current = get().properties;
        const prop = current.find(p => p.id === propertyId);
        if (!prop) return null;

        const newOption = {
            id: option.id || crypto.randomUUID(),
            label: option.label,
            color: option.color || getRandomPillColor(),
        };

        const updatedProp = {
            ...prop,
            options: [...(prop.options || []), newOption],
        };

        const next = current.map(p => p.id === propertyId ? updatedProp : p);
        set({ properties: next });
        storage.set('properties', next);
        return newOption;
    }
}));

// Random pill color palette for new options
const PILL_COLORS = ['#2383E2', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316', '#6366F1', '#14B8A6'];
function getRandomPillColor() {
    return PILL_COLORS[Math.floor(Math.random() * PILL_COLORS.length)];
}

onRehydrate(() => {
    usePropertyStore.setState({ properties: storage.get('properties', []) });
});

export default usePropertyStore;
